import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import cacheManager from '../CacheManager';
import { formatDate } from '../js/dateFormatter';
import Select from 'react-select';
import { NumericFormat } from 'react-number-format';
import { showSuccess, showError } from '../utils/toast';

const PgPagamentoEditar = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(true);

    const [clientesById, setClientesById] = useState({});
    const [faturasById, setFaturasById] = useState({});
    const [pagamento, setPagamento] = useState({
        id_cliente: '',
        id_fatura: '',
        data_pagamento: '',
        valor: '',
        forma_pagamento: '',
        data_extorno: '',
    });

    const carregarCache = async () => {
        try {
            setIsLoading(true);
            const clientesById = await cacheManager.getClientesById();
            const faturasById = await cacheManager.getFaturasById();
            setClientesById(clientesById);
            setFaturasById(faturasById);
        } catch (error) {
            console.error('Erro ao buscar clientes e faturas:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const carregarDados = async () => {
            await carregarCache();

            if (id) {
                setIsLoading(true);
                fetch(`https://api.faeworks.com.br/pagamento/read.php?id=${id}`)
                    .then(response => response.json())
                    .then(data => {
                        if (data && data.length > 0) {
                            const pagamento = data[0];
                            setPagamento({
                                id_cliente: pagamento.id_cliente,
                                id_fatura: pagamento.id_fatura,
                                data_pagamento: pagamento.data_pagamento ? pagamento.data_pagamento.split(' ')[0] : '',
                                valor: pagamento.valor,
                                forma_pagamento: pagamento.forma_pagamento,
                                data_extorno: pagamento.data_extorno ? pagamento.data_extorno.split(' ')[0] : ''
                            });
                        } else {
                            showError('Erro ao buscar dados do pagamento');
                        }
                    })
                    .catch(error => console.error('Erro ao buscar dados do pagamento:', error))
                    .finally(() => setIsLoading(false));
            }
        };

        carregarDados();
    }, [id]);

    const atualizarPagamento = async () => {
        try {
            setIsLoading(true);
            const url = id ? 'https://api.faeworks.com.br/pagamento/update.php' : 'https://api.faeworks.com.br/pagamento/create.php';
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id_pagamento: id,
                    id_cliente: pagamento.id_cliente,
                    id_fatura: pagamento.id_fatura,
                    data_pagamento: pagamento.data_pagamento,
                    valor: pagamento.valor,
                    forma_pagamento: pagamento.forma_pagamento,
                    data_extorno: pagamento.data_extorno,
                }),
            });
            const result = await response.json();
            if (result.status === 'success') {
                setIsLoading(false);
                showSuccess('Pagamento atualizado com sucesso!');
                cacheManager.invalidatePagamentosCache();
                handleVoltarClick();
            } else {
                setIsLoading(false);
                showError('Erro ao atualizar pagamento: ' + result.message);
            }
        } catch (error) {
            console.error('Erro ao atualizar pagamento:', error);
            setIsLoading(false);
            showError('Erro ao atualizar pagamento.');
        }
    };

    // Estilos para esmaecer a página
    const overlayStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 2,
        display: isLoading ? 'block' : 'none',
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setPagamento({ ...pagamento, [name]: value });
    };

    const handleValorChange = (values) => {
        const { floatValue } = values;
        setPagamento({ ...pagamento, valor: floatValue });
    };

    const handleVoltarClick = () => {
        navigate('/pagamentos');
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        await atualizarPagamento();
    };

    const handleClienteChange = (selectedOption) => {
        setPagamento({ ...pagamento, id_cliente: selectedOption ? selectedOption.value : '' });
    };

    const handleFaturaChange = (selectedOption) => {
        setPagamento({ ...pagamento, id_fatura: selectedOption ? selectedOption.value : '' });
    };

    const clienteOptions = Object.keys(clientesById).map((key) => ({
        value: key,
        label: (clientesById[key].cpf ? clientesById[key].cpf + ' | ' : '') + clientesById[key].nome
    }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const tenMonthsAgo = new Date();
    tenMonthsAgo.setMonth(tenMonthsAgo.getMonth() - 10);

    const faturaOptions = Object.keys(faturasById)
        .filter((key) => new Date(faturasById[key].data_emissao) >= tenMonthsAgo)
        .map((key) => ({
            value: key,
            label: `Fatura ${key} - ${formatDate(faturasById[key].data_emissao)} - R$${faturasById[key].valor_total}`
        }))
        .sort((a, b) => a.value < b.value);

    return (
        <div className="container mt-5">
            {isLoading && <div style={overlayStyle}></div>} {/* Overlay de carregamento */}
            <h1 className="h1">{id ? 'Editar Pagamento' : 'Novo Pagamento'}</h1>
            <form onSubmit={handleSubmit} className="needs-validation" noValidate>
                <div className="row">
                    <div className="col-12 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Cliente:
                            <Select
                                className="form-select"
                                value={clienteOptions.find(option => option.value === String(pagamento.id_cliente))}
                                onChange={handleClienteChange}
                                options={clienteOptions}
                                isClearable={false}
                            />
                        </label>
                    </div>
                    <div className="col-12 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Fatura:
                            <Select
                                className="form-select"
                                value={faturaOptions.find(option => option.value === String(pagamento.id_fatura))}
                                onChange={handleFaturaChange}
                                options={faturaOptions}
                                isClearable={false}
                            />
                        </label>
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Data de Pagamento:
                            <input
                                type="date"
                                className="form-control"
                                name="data_pagamento"
                                value={pagamento.data_pagamento}
                                onChange={handleInputChange}
                            />
                        </label>
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Valor:
                            <NumericFormat
                                className="form-control"
                                name="valor"
                                value={pagamento.valor}
                                onValueChange={handleValorChange}
                                thousandSeparator="."
                                decimalSeparator=","
                                decimalScale={2}
                                fixedDecimalScale={true}
                                allowNegative={false}
                                prefix={'R$ '}
                            />
                        </label>
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Forma de Pagamento:
                            <select
                                className="form-control"
                                name="forma_pagamento"
                                value={pagamento.forma_pagamento}
                                onChange={handleInputChange}
                            >
                                <option value="">-- -- --</option>
                                <option value="PIX">PIX</option>
                                <option value="Débito">Débito</option>
                                <option value="Débito Master">Débito Master</option>
                                <option value="Débito Visa">Débito Visa</option>
                                <option value="Débito Elo">Débito Elo</option>
                                <option value="Crédito">Crédito</option>
                                <option value="Crédito Master">Crédito Master</option>
                                <option value="Crédito Visa">Crédito Visa</option>
                                <option value="Crédito Elo">Crédito Elo</option>
                                <option value="Boleto">Boleto</option>
                                <option value="Espécie">Espécie</option>
                                <option value="Cheque">Cheque</option>
                                <option value="Depósito Bancario">Depósito Bancario</option>
                            </select>
                        </label>
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Extornado em:
                            <input
                                type="date"
                                className="form-control"
                                name="data_extorno"
                                value={pagamento.data_extorno}
                                onChange={handleInputChange}>
                                </input>
                        </label>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12 d-flex justify-content-end">
                        <button
                            type="button"
                            className="btn btn-secondary me-2"
                            onClick={handleVoltarClick}
                        >
                            Voltar
                        </button>
                        <button type="submit" className="btn btn-primary">
                            Salvar
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default PgPagamentoEditar;
